import React from 'react';
import { Modal, Text, Button, Group, Stack, List, ThemeIcon, Box, Divider } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconCheck, IconMicrophone, IconCrown, IconHistory, IconChartBar, IconMessage, IconBrain, IconHeart, IconStars } from '@tabler/icons-react';
import { Check } from 'lucide-react';

interface UpgradeModalProps {
  opened: boolean;
  onClose: () => void;
  feature?: 'voice' | 'history' | 'limit';
  title?: string;
  description?: string;
  currentTier?: string;
}

/**
 * Modal shown when a user tries to access a Voice Connect feature
 * or has hit their conversation limit
 */
export function UpgradeModal({
  opened,
  onClose,
  feature = 'voice',
  title,
  description,
  currentTier
}: UpgradeModalProps) {
  console.log('[UpgradeModal] Rendering with props:', { 
    opened, 
    feature, 
    currentTier 
  });
  // Enhanced titles and descriptions based on feature
  const defaultTitles = {
    voice: 'Elevate Your Experience with Voice',
    history: 'Never Lose a Meaningful Insight',
    limit: 'Begin Your Wellness Journey'
  };

  const defaultDescriptions = {
    voice: 'Express yourself naturally through voice conversations – a more personal way to connect with your emotional guide.',
    history: 'Your journey is valuable. Voice Connect gives you extendedunlimited conversation history, so every insight stays with you forever.',
    limit: 'Start your path to emotional well-being today. Choose Text or Voice Connect to unlock meaningful conversations designed to support your growth.'
  };

  const modalTitle = title || defaultTitles[feature];
  const modalDescription = description || defaultDescriptions[feature];

  // Feature-specific icon
  const FeatureIcon = {
    voice: IconMicrophone,
    history: IconHistory,
    limit: IconCrown
  }[feature];

  // Feature categories with icons
  const premiumFeatures = [
    {
      category: "Communication",
      icon: IconMessage,
      features: ["Text AND voice conversations", "Adaptive conversation experience"]
    },
    {
      category: "Insights",
      icon: IconBrain,
      features: ["Advanced emotional awareness building", "Personalized growth insights"]
    },
    {
      category: "Wellness",
      icon: IconHeart,
      features: ["Extended conversation history", "Tailored emotional wellness tips"]
    }
  ];

  const continueButtonText = currentTier?.toLowerCase() === 'basic' 
    ? 'Continue with Text Companion' 
    : 'Continue with Free';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title=""
      centered
      size="lg"
      styles={{
        body: { padding: 0 },
        header: { padding: 0, margin: 0 },
        content: { padding: 0, overflow: 'hidden' }
      }}
    >
      {/* Modal header with gradient background */}
      <div className="bg-gradient-to-r from-[#C2A4D6]/30 to-[#F4B6A1]/30 p-6 text-center">
        <div className="inline-block bg-white p-4 rounded-full mb-4 shadow-sm">
          <ThemeIcon size={60} radius={100} className="bg-[#C2A4D6]">
            <FeatureIcon size={30} />
          </ThemeIcon>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2 font-['Poppins']">
          {modalTitle}
        </h2>
        <p className="text-gray-600 mb-0">
          {modalDescription}
        </p>
      </div>

      <div className="p-6">
        {/* Features section */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
          <div className="bg-[#C2A4D6]/10 p-4 border-b border-gray-100">
            <Text fw={600} size="lg" className="text-[#C2A4D6]">Premium features designed for your wellbeing</Text>
          </div>

          <div className="p-4">
            <div className="grid md:grid-cols-3 gap-4">
              {premiumFeatures.map((category, i) => (
                <div key={i} className="p-3">
                  <div className="flex items-center mb-2">
                    <ThemeIcon size={28} radius="xl" className="bg-[#C2A4D6] mr-2">
                      <category.icon size={16} />
                    </ThemeIcon>
                    <Text fw={600} size="sm">{category.category}</Text>
                  </div>
                  <ul className="space-y-2 pl-2">
                    {category.features.map((feat, j) => (
                      <li key={j} className="flex items-start text-sm">
                        <Check className="w-4 h-4 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonial/social proof */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6 text-center italic text-gray-600 text-sm">
          <IconStars size={16} className="text-[#F4B6A1] inline-block mr-1" />
          <span>"95% of our FeelHeard Companion users report improved emotional awareness after just 2 weeks."</span>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
          <Button 
            variant="default" 
            onClick={onClose}
            className="order-2 md:order-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 transition-colors"
            radius="xl"
            size="md"
          >
            {continueButtonText}
          </Button>
          <Button 
            component={Link} 
            to="/settings/subscription" 
            onClick={onClose}
            className="order-1 md:order-2 bg-[#C2A4D6] hover:bg-[#B390C9] text-white w-full md:w-auto transition-colors"
            radius="xl"
            size="md"
          >
            Upgrade to Feel Heard
          </Button>
        </div>

        {/* Guarantee text */}
        <Text ta="center" c="dimmed" size="xs" mt="md">
          Upgrade now for full access. Easy cancellation anytime.
        </Text>
      </div>
    </Modal>
  );
}